/**
 * Today's (or the last session's) intraday bars for the stock page's 1D chart,
 * from GET /live/candles and /live/quotes (src/rai/api/routes/live.py).
 * Those routes return untyped-for-us shapes, so they are described here.
 */
import type { Candle, Overlay } from "@/components/chart";
import { DASH } from "@/lib/format";

export const INTERVALS = ["5m", "1m"] as const;
export type Interval = (typeof INTERVALS)[number];
export const DEFAULT_INTERVAL: Interval = "5m";

export interface LiveBar {
  ts: string;
  open: number | null;
  high: number | null;
  low: number | null;
  close: number | null;
  volume: number | null;
  oi: number | null;
  source: string;
}

export interface LiveCandles {
  key: string;
  symbol: string;
  exchange: string;
  kind: string;
  interval: Interval;
  session_date: string | null;
  live_bars: number;
  candles: LiveBar[];
}

export interface LiveQuote {
  key: string;
  symbol: string;
  exchange: string;
  kind: string;
  ltp: number | null;
  prev_close: number | null;
  change: number | null;
  change_pct: number | null;
  session_date: string | null;
  last_update: string | null;
  source: string;
}

export interface LiveQuotes {
  now: string;
  any_open: boolean;
  session_date: string | null;
  quotes: LiveQuote[];
}

const STEP_MS: Record<Interval, number> = { "1m": 60_000, "5m": 300_000 };

/**
 * The stored bars, with the last one carried forward to the latest quote so
 * the chart moves between bar refreshes while the market is open.
 */
export function withLiveBar(
  bars: LiveCandles | undefined,
  quote: LiveQuote | undefined,
  live: boolean,
  interval: Interval,
): Candle[] {
  const rows: Candle[] = (bars?.candles ?? []).flatMap((b) =>
    b.open == null || b.high == null || b.low == null || b.close == null
      ? []
      : [{ time: b.ts, open: b.open, high: b.high, low: b.low, close: b.close, volume: b.volume }],
  );
  const last = rows.at(-1);
  const ltp = quote?.ltp;
  if (!live || !last || ltp == null || !quote?.last_update || quote.session_date !== bars?.session_date) return rows;
  const step = STEP_MS[interval];
  const lastStart = Date.parse(last.time);
  const at = Date.parse(quote.last_update);
  if (at >= lastStart + step) {
    const start = new Date(Math.floor(at / step) * step).toISOString();
    rows.push({ time: start, open: last.close, high: Math.max(last.close, ltp), low: Math.min(last.close, ltp), close: ltp, volume: null });
  } else if (at >= lastStart) {
    rows[rows.length - 1] = { ...last, high: Math.max(last.high, ltp), low: Math.min(last.low, ltp), close: ltp };
  }
  return rows;
}

/** Session VWAP from the bars (typical price × volume); none for indices, which have no volume. */
export function vwapOverlay(candles: Candle[]): Overlay | null {
  let pv = 0;
  let v = 0;
  const data: Overlay["data"] = [];
  for (const c of candles) {
    const vol = c.volume ?? 0;
    pv += ((c.high + c.low + c.close) / 3) * vol;
    v += vol;
    data.push({ time: c.time, value: v > 0 ? pv / v : null });
  }
  return v > 0 ? { id: "vwap", title: "VWAP", color: "accent", lineWidth: 1, data } : null;
}

const istHMS = new Intl.DateTimeFormat("en-IN", {
  timeZone: "Asia/Kolkata",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: false,
});
const istHM = new Intl.DateTimeFormat("en-IN", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit", hour12: false });

/** "10:42:07" (IST), or "10:42" without seconds. */
export function timeIST(value: string | null | undefined, seconds = true): string {
  if (!value) return DASH;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return DASH;
  return (seconds ? istHMS : istHM).format(d);
}
